import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MdNotificationsActive, MdCalendarMonth, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { MONTHS, guessCategory, TaxEvent } from "data/tax-calendar";
import { getYearEvents, getWidgetDefault, shiftMonth, isTahmini } from "data/tax-calendar-years";

const WEEK_DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const CATEGORY_DOT: Record<string, string> = {
  "KDV": "bg-red-500",
  "Muhtasar": "bg-blue-500",
  "Damga": "bg-amber-500",
  "Geçici Vergi": "bg-orange-500",
  "Kurumlar": "bg-indigo-500",
  "Gelir Vergisi": "bg-pink-500",
  "SGK": "bg-teal-500",
  "ÖTV": "bg-cyan-500",
  "e-Defter": "bg-slate-500",
  "MTV": "bg-yellow-500",
  "Emlak": "bg-emerald-500",
  "Harç": "bg-lime-500",
  "Diğer": "bg-gray-500",
};

function pad(n: number) { return n < 10 ? `0${n}` : `${n}`; }

function buildEventsByDate(year: number, month: number): Record<string, TaxEvent[]> {
  const out: Record<string, TaxEvent[]> = {};
  for (const e of getYearEvents(year)) {
    const m = parseInt(e.date.split("-")[0], 10);
    if (m !== month) continue;
    if (!out[e.date]) out[e.date] = [];
    out[e.date].push(e);
  }
  return out;
}

function buildMonthGrid(year: number, month: number): (number | null)[] {
  const first = new Date(year, month - 1, 1);
  // Pzt = 0
  const startWeekday = (first.getDay() + 6) % 7;
  const lastDay = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function HomeCalendarWidget() {
  // Açılış: bugünün yılı/ayı/günü. Bugünün yılının verisi yoksa en yakın veri
  // yılı açılır ve bu AÇIKÇA yazılır (sessizce başka yıl gösterilmez).
  const [now] = useState(() => new Date());
  const initial = useMemo(() => getWidgetDefault(now), [now]);
  const todayYear = now.getFullYear();
  const todayMonth = now.getMonth() + 1;
  const todayDay = now.getDate();

  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.monthNum);

  const eventsByDate = useMemo(() => buildEventsByDate(year, month), [year, month]);
  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  // Default seçili gün: bugün (doğru yıl/ayda ise) → ilk event günü → 1
  const [selectedDay, setSelectedDay] = useState<number>(() => {
    if (initial.bugununYiliVar) return initial.day;
    const firstKey = Object.keys(buildEventsByDate(initial.year, initial.monthNum)).sort()[0];
    return firstKey ? parseInt(firstKey.split("-")[1], 10) : 1;
  });

  // Yıl sınırını geçebilir (Aralık → ertesi yılın Ocak ayı); verisi olmayan yıla geçmez.
  const prevTarget = shiftMonth(year, month, -1);
  const nextTarget = shiftMonth(year, month, 1);

  // Ay değiştiğinde seçili günü resetle
  const onChangeMonth = (delta: number) => {
    const target = shiftMonth(year, month, delta);
    if (!target) return;
    setYear(target.year);
    setMonth(target.monthNum);
    const nextEvents = buildEventsByDate(target.year, target.monthNum);
    const firstKey = Object.keys(nextEvents).sort()[0];
    if (target.year === todayYear && target.monthNum === todayMonth) {
      setSelectedDay(todayDay);
    } else if (firstKey) {
      setSelectedDay(parseInt(firstKey.split("-")[1], 10));
    } else {
      setSelectedDay(1);
    }
  };

  const tahmini = isTahmini(year);
  const monthInfo = MONTHS.find(m => m.num === month)!;
  const selectedKey = `${pad(month)}-${pad(selectedDay)}`;
  const selectedEvents = eventsByDate[selectedKey] || [];

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-xl shadow-brand-500/5 sm:p-5">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-500 text-white">
            <MdCalendarMonth className="h-4 w-4" />
          </span>
          <h3 className="font-outfit text-base font-bold text-navy-700">Vergi Takvimi</h3>
        </div>
        <Link to="/vergi-takvimi" className="text-xs font-semibold text-brand-500 hover:text-brand-600">
          Tümünü Gör →
        </Link>
      </div>

      {/* Ay Navigasyon */}
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          aria-label="Önceki ay"
          onClick={() => onChangeMonth(-1)}
          disabled={!prevTarget}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 disabled:opacity-30"
        >
          <MdChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-sm font-bold text-navy-700">
          {monthInfo.name} {year}
          {tahmini && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title="GİB bu yılın takvimini henüz yayımlamadı; tarihler tahminidir.">Tahmini</span>}
        </div>
        <button
          type="button"
          aria-label="Sonraki ay"
          onClick={() => onChangeMonth(1)}
          disabled={!nextTarget}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 disabled:opacity-30"
        >
          <MdChevronRight className="h-5 w-5" />
        </button>
      </div>

      {!initial.bugununYiliVar && (
        <p className="mb-2 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600">
          {todayYear} vergi takvimi henüz eklenmedi; {initial.year} gösteriliyor.
        </p>
      )}
      {tahmini && (
        <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          Tahmini takvim — GİB {year} takvimi yayımlanınca güncellenecek. Resmî tarihleri gib.gov.tr'den teyit edin.
        </p>
      )}

      {/* Hafta günleri */}
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {WEEK_DAYS.map(d => (
          <div key={d} className="py-1">{d}</div>
        ))}
      </div>

      {/* Gün gridi */}
      <div className="grid grid-cols-7 gap-1">
        {grid.map((day, i) => {
          if (day === null) return <div key={i} className="aspect-square" />;
          const key = `${pad(month)}-${pad(day)}`;
          const hasEvent = !!eventsByDate[key];
          const isToday = year === todayYear && month === todayMonth && day === todayDay;
          const isSelected = day === selectedDay;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={`relative flex aspect-square items-center justify-center rounded-lg text-xs font-medium transition ${
                isSelected
                  ? "bg-brand-500 text-white shadow-md shadow-brand-500/30"
                  : isToday
                  ? "bg-brand-50 text-brand-600 ring-1 ring-brand-300"
                  : hasEvent
                  ? "text-navy-700 hover:bg-brand-50"
                  : "text-gray-400 hover:bg-gray-50"
              }`}
            >
              {day}
              {hasEvent && !isSelected && (
                <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brand-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Seçili gün detay */}
      <div className="mt-4 rounded-xl bg-gray-50 p-3">
        <div className="mb-2 flex items-baseline justify-between">
          <div className="text-xs font-bold text-navy-700">
            {selectedDay} {monthInfo.name} {year}
          </div>
          <div className="text-[10px] text-gray-500">
            {selectedEvents.length > 0 ? `${selectedEvents.length} etkinlik` : "Etkinlik yok"}
          </div>
        </div>
        {selectedEvents.length > 0 ? (
          <ul className="space-y-1.5 max-h-32 overflow-y-auto">
            {selectedEvents.slice(0, 4).map((e, i) => {
              const cat = guessCategory(e);
              return (
                <li key={i} className="flex items-start gap-2 text-[11px] leading-snug">
                  <span className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${CATEGORY_DOT[cat] || CATEGORY_DOT["Diğer"]}`} />
                  <span className="text-gray-700 line-clamp-2">{e.description}</span>
                </li>
              );
            })}
            {selectedEvents.length > 4 && (
              <li className="pl-3.5 text-[10px] text-gray-500">+{selectedEvents.length - 4} daha…</li>
            )}
          </ul>
        ) : (
          <p className="text-[11px] text-gray-500">Bu tarih için bildirim/ödeme yok.</p>
        )}
      </div>

      {/* CTA — Hatırlatma Al (üyelik kaydı) */}
      <Link
        to="/auth/sign-in"
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-center text-xs font-bold text-white shadow-md shadow-brand-500/20 transition hover:bg-brand-600"
      >
        <MdNotificationsActive className="h-4 w-4" />
        <span>Tüm Tarihler İçin Ücretsiz Hatırlatma Al</span>
      </Link>
    </div>
  );
}
